import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { api } from "@src/services/api/client";
import type { Livestream } from "@src/services/api/cameras-api";
import { getResolvedApiBaseUrl } from "@src/services/api/request";
import { useTenantId } from "@src/features/tenants";
import { useCachingConfig } from "@src/config/caching-config";
import { selectLivestream } from "@src/utils/camera-capabilities";

export type UseLivestreamOptions = {
  siteId?: string;
  cameraKey: string;
  enabled?: boolean;
};

function resolveStreamUrl(livestreams: Livestream[] | undefined, apiBaseUrl: string | undefined): string | undefined {
  const selected = selectLivestream(livestreams);
  if (!selected) return undefined;
  const start = selected.session.start;
  if (start.startsWith("http")) return start;
  if (apiBaseUrl) return `${apiBaseUrl}${start}`;
  return undefined;
}

/**
 * Manages the livestream session for a camera.
 *
 * The camera query re-mints its signed livestream URL on every refetch (the
 * 5-min refresh, focus, reconnect), but the URL a player is watching is frozen
 * on first adoption and only advances via renewSession(). Binding the player to
 * the freshest URL instead would reload it on unrelated refetches, opening a new
 * proxied stream to the edge device each time — which costs real money and is
 * the "duplicate Livestream ended every 5 min" bug this exists to prevent.
 *
 * renewSession() is the only path that advances the URL. It awaits a refetch so
 * the adopted URL is freshly signed rather than a cached one that may have
 * expired (signed URLs live 1h; a long attended session can outlast that).
 */
export function useLivestream({ siteId, cameraKey, enabled = true }: UseLivestreamOptions) {
  const tenantId = useTenantId();
  const cachingConfig = useCachingConfig();
  if (!siteId) {
    console.error("useLivestream: siteId is required");
  }
  const effectiveSiteId = siteId || "";

  const cameraQuery = useQuery({
    queryKey: ["cameras", tenantId, effectiveSiteId, cameraKey],
    queryFn: () => api.cameras.get(tenantId, effectiveSiteId, cameraKey),
    enabled: enabled && !!tenantId && !!cameraKey,
    staleTime: cachingConfig.cameras.snapTtlSec * 1000,
    placeholderData: keepPreviousData,
  });

  const selectedLivestream = selectLivestream(cameraQuery.data?.livestreams);

  const apiBaseQuery = useQuery({
    queryKey: ["api-base-url"],
    queryFn: getResolvedApiBaseUrl,
    staleTime: Infinity,
  });

  const derivedUrl = useMemo(
    () => resolveStreamUrl(cameraQuery.data?.livestreams, apiBaseQuery.data),
    [cameraQuery.data?.livestreams, apiBaseQuery.data],
  );

  // Freeze the URL on first adoption so unrelated camera refetches (the sibling
  // useCamera's refetchInterval re-mints the signed URL) don't reload the player.
  // Consumers key their host on tenant/site/camera so a different camera remounts
  // and re-adopts, keeping this hook free of any switch-mid-flight reconciliation.
  const [sessionUrl, setSessionUrl] = useState<string | undefined>(undefined);
  if (!sessionUrl && derivedUrl) {
    setSessionUrl(derivedUrl);
  }

  const refetchCamera = cameraQuery.refetch;

  const renewSession = useCallback(async (): Promise<string | undefined> => {
    const result = await refetchCamera();
    const fresh = resolveStreamUrl(result.data?.livestreams, apiBaseQuery.data);
    if (fresh) setSessionUrl(fresh);
    return fresh;
  }, [refetchCamera, apiBaseQuery.data]);

  return {
    camera: cameraQuery.data,
    isCameraLoading: cameraQuery.isLoading,
    cameraError: cameraQuery.error,
    renewSession,

    livestream: selectedLivestream,
    hasLivestream: !!selectedLivestream,
    streamFormat: selectedLivestream?.format,

    streamUrl: sessionUrl,
  };
}
